import { useCallback } from 'react';
import { type FileRejection, type FileWithPath } from 'react-dropzone';

import { type OnboardingAnalyticsCategory, useTrackCompletedOnboardingUpload } from '@air/analytics';
import { type Board } from '@air/api/types';
import { useAccount } from '@air/utils-auth';

import { useOnboardingPreferencesStore } from '~/components/v2/store/features/onboarding/useOnboardingPreferencesStore';
import { useGoToPrivateBoardPage } from '~/hooks/useGoToPrivateBoardPage';
import { splitFilesByFolder, useQueuePrivateUploads } from '~/hooks/useQueuePrivateUploads';
import { useUploadValidation } from '~/hooks/useUploadValidation';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useCreateNewBoard } from '~/swr-hooks/boards/useCreateNewBoard';

export type SubmitOnboardingUploadParams = {
  acceptedFiles: readonly FileWithPath[];
  board?: Board;
  category: OnboardingAnalyticsCategory;
  fileRejections?: readonly FileRejection[];
  skipBoardCreation?: boolean;
};

export const useSubmitOnboardingUpload = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const { validateUpload } = useUploadValidation({ workspaceId });
  const { queuePrivateUploads } = useQueuePrivateUploads();
  const clearShowFirstUploadPrompt = useOnboardingPreferencesStore((state) => state.clearShowFirstUploadPrompt);
  const { trackCompletedOnboardingUpload } = useTrackCompletedOnboardingUpload();
  const { data: account } = useAccount();
  const { createNewBoard } = useCreateNewBoard();
  const { goToBoardPage } = useGoToPrivateBoardPage();

  const submitOnboardingUpload = useCallback(
    async ({
      acceptedFiles,
      board: existingBoard,
      category,
      fileRejections = [],
      skipBoardCreation = false,
    }: SubmitOnboardingUploadParams) => {
      if (!workspaceId) {
        return;
      }

      const validatedFiles = await validateUpload([...acceptedFiles]);

      if (!validatedFiles) {
        return;
      }

      const { files, emptyFiles, filesWithNoExtension } = validatedFiles;
      const [filesInFolders, filesNotInFolders] = splitFilesByFolder(files);
      const topLevelFilesToUpload = filesNotInFolders.map((file) => ({ file }));

      let board = existingBoard;

      if (!board && !skipBoardCreation) {
        try {
          board = await createNewBoard({
            board: { title: `${account?.firstName || 'User'}'s first board` },
            trackLocation: 'workspace-onboarding',
            shouldFollow: true,
          });
        } catch (_error) {
          // do nothing
        }
      }

      clearShowFirstUploadPrompt(workspaceId);

      if (board && !existingBoard) {
        await goToBoardPage({ board, method: 'replace' });
      }

      // biome-ignore lint/nursery/noFloatingPromises: PLA-1724 — pre-existing debt, not introduced by the icon migration
      queuePrivateUploads({
        filesInFolders,
        topLevelFilesToUpload,
        rejectedFiles: fileRejections.map(({ file }) => file),
        emptyFiles,
        boardToUpload: board,
        filesWithNoExtension,
      });
      trackCompletedOnboardingUpload({ category });
    },
    [
      account?.firstName,
      clearShowFirstUploadPrompt,
      createNewBoard,
      goToBoardPage,
      queuePrivateUploads,
      trackCompletedOnboardingUpload,
      validateUpload,
      workspaceId,
    ],
  );

  return { submitOnboardingUpload };
};
